import { useState, useEffect, useCallback } from 'react';
import { BACKEND_URL } from '@/lib/backend';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { getInitials } from '@/lib/utils';
import { Loader2, MapPin, Search, Crosshair, ExternalLink, RefreshCw, Clock, Users } from 'lucide-react';
import { PingTrailMap, LiveAgentsMap } from './PingMaps';
import { companySettingsAPI } from '@/lib/api';

const fetchJson = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}`, ...(opts.headers || {}) },
  });
  if (!res.ok) throw new Error(await res.text());
  return res.status === 204 ? null : res.json();
};

function relativeTime(iso) {
  if (!iso) return 'Never';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'Just now';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function AgentLocationCrumbs() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [trail, setTrail] = useState([]);
  const [trailLoading, setTrailLoading] = useState(false);
  const [pingingId, setPingingId] = useState(null);
  // Maps are rendered with OpenStreetMap (Leaflet) — no API key needed.
  // "use the Leaflet fallback". Loaded once on mount.

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchJson('/api/location-pings/users');
      setUsers(data || []);
    } catch (err) {
      console.error('Failed to load users with pings', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30_000); // auto-refresh every 30s
    return () => clearInterval(t);
  }, [load]);



  const loadTrail = useCallback(async (userId) => {
    try {
      setTrailLoading(true);
      const data = await fetchJson(`/api/location-pings?user_id=${userId}&limit=50`);
      setTrail(data || []);
    } catch (err) {
      console.error('Failed to load trail', err);
      setTrail([]);
    } finally {
      setTrailLoading(false);
    }
  }, []);

  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const handleRequestPing = async (userId) => {
    try {
      setPingingId(userId);
      await fetchJson(`/api/location-pings/request/${userId}`, { method: 'POST' });
      await load();
      setTimeout(() => setPingingId(null), 2500);
    } catch (err) {
      alert('Failed to queue ping request');
      setPingingId(null);
    }
  };

  const handleSelectUser = (user) => {
    setSelected(user);
    if (user) loadTrail(user.id);
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-50" data-testid="agent-location-crumbs">
      <div className="p-4 md:p-6 lg:p-8 max-w-[1400px] mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="w-7 h-7 text-cyan-500" />Live Map &amp; Trails
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Last-known location of every agent / admin on a single map, plus per-agent
              breadcrumb trails. Live overview auto-refreshes every 30s.
            </p>
          </div>
          <Button variant="outline" onClick={load} className="rounded-full h-10 self-start" data-testid="loc-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />Refresh
          </Button>
        </div>

        {/* Combined live map — every recently-pinged agent on one map */}
      <Card className="bg-white border-slate-200 mb-4" data-testid="live-overview-card">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2 px-1">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Users className="w-4 h-4 text-cyan-500" /> Live overview
              </h2>
              <span className="text-xs text-slate-400" data-testid="live-overview-count">
                {(() => {
                  const liveAgents = users.filter((u) => u.latest_ping && Number.isFinite(u.latest_ping.latitude));
                  return `${liveAgents.length} agent${liveAgents.length === 1 ? '' : 's'} on map`;
                })()}
              </span>
            </div>
            {loading && users.length === 0 ? (
              <div className="h-[420px] flex items-center justify-center text-slate-500">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading map…
              </div>
            ) : (
              <LiveAgentsMap
                agents={users
                  .filter((u) => u.latest_ping && Number.isFinite(u.latest_ping.latitude))
                  .map((u) => ({
                    user_id: u.id,
                    name: u.name,
                    role: u.role,
                    tier: u.tier,
                    latitude: u.latest_ping.latitude,
                    longitude: u.latest_ping.longitude,
                    accuracy: u.latest_ping.accuracy,
                    timestamp: u.latest_ping.timestamp,
                  }))}
                height="420px"
              />
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* User list */}
          <Card className="lg:col-span-1 bg-white border-slate-200">
            <CardContent className="p-3">
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search agent..."
                  className="pl-10 h-10 bg-white border-slate-200"
                  data-testid="loc-search-input"
                />
              </div>
              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
              ) : filtered.length === 0 ? (
                <p className="text-sm text-slate-400 py-6 text-center">No agents found.</p>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="loc-user-list">
                  {filtered.map((u) => {
                    const lp = u.latest_ping;
                    const isActive = selected?.id === u.id;
                    return (
                      <li key={u.id}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleSelectUser(u)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSelectUser(u); } }}
                          className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${isActive ? 'bg-cyan-50 border border-cyan-200' : 'hover:bg-slate-50 border border-transparent'}`}
                          data-testid={`loc-user-${u.id}`}
                          aria-label={`Select ${u.name}`}
                        >
                          <Avatar className="w-9 h-9 border border-slate-200">
                            <AvatarFallback className="bg-cyan-100 text-cyan-700 text-[11px] font-semibold">{getInitials(u.name)}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-sm text-slate-800 truncate">{u.name}</span>
                              <Badge variant="outline" className="text-[10px] capitalize border-slate-200 text-slate-500 px-1.5 py-0 h-4">{u.role}{u.tier ? ` · T${u.tier}` : ''}</Badge>
                            </div>
                            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 mt-0.5">
                              <Clock className="w-3 h-3" />
                              <span>{lp ? relativeTime(lp.timestamp) : 'No pings yet'}</span>
                              {u.ping_pending && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 h-4">Ping queued</Badge>}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={`h-9 w-9 rounded-full ${pingingId === u.id ? 'bg-cyan-100 text-cyan-600' : 'text-cyan-600 hover:bg-cyan-50'}`}
                            onClick={(e) => { e.stopPropagation(); handleRequestPing(u.id); }}
                            data-testid={`loc-ping-btn-${u.id}`}
                            aria-label={`Request a fresh ping from ${u.name}`}
                            title="Request a fresh ping (silent — agent is not notified)"
                          >
                            {pingingId === u.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Detail */}
          <Card className="lg:col-span-2 bg-white border-slate-200">
            <CardContent className="p-4">
              {!selected ? (
                <div className="text-center text-slate-500 py-10">
                  <MapPin className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">Select an agent to view their location trail.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-3 mb-4">
                    <Avatar className="w-11 h-11 border border-slate-200">
                      <AvatarFallback className="bg-cyan-100 text-cyan-700 font-semibold">{getInitials(selected.name)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h2 className="text-lg font-bold text-slate-900 leading-tight truncate">{selected.name}</h2>
                      <p className="text-sm text-slate-500 truncate">{selected.email}</p>
                      <div className="flex flex-wrap gap-2 mt-1.5">
                        <Badge variant="outline" className="capitalize border-slate-200 text-slate-600 text-[11px]">{selected.role}{selected.tier ? ` · Tier ${selected.tier}` : ''}</Badge>
                        {selected.availability_status && <Badge variant="outline" className="capitalize border-slate-200 text-slate-600 text-[11px]">{selected.availability_status}</Badge>}
                      </div>
                    </div>
                    <Button onClick={() => handleRequestPing(selected.id)} disabled={pingingId === selected.id} className="rounded-full bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="loc-detail-ping-btn">
                      {pingingId === selected.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Crosshair className="w-4 h-4 mr-2" />}
                      Ping now
                    </Button>
                  </div>

                  {/* Latest ping panel */}
                  {selected.latest_ping ? (
                    <div className="rounded-xl border border-cyan-200 bg-cyan-50/40 p-3 mb-4" data-testid="loc-latest-panel">
                      <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Most recent ping</p>
                      <p className="text-sm text-slate-800 mt-1">
                        {selected.latest_ping.latitude?.toFixed(5)}, {selected.latest_ping.longitude?.toFixed(5)}
                        {selected.latest_ping.accuracy ? <span className="text-slate-500"> (±{Math.round(selected.latest_ping.accuracy)}m)</span> : null}
                      </p>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="text-xs text-slate-500">{relativeTime(selected.latest_ping.timestamp)} · {new Date(selected.latest_ping.timestamp).toLocaleString()}</span>
                        <a
                          href={`https://www.openstreetmap.org/?mlat=${selected.latest_ping.latitude}&mlon=${selected.latest_ping.longitude}&zoom=16`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-xs text-cyan-600 hover:underline inline-flex items-center gap-1"
                        >
                          Open in map <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 p-3 mb-4 text-sm text-slate-500">No pings yet for this agent. Click <strong>Ping now</strong> above to silently request a fresh location.</div>
                  )}

                  {/* Trail map (newest → oldest connected by polyline) */}
                  {trail.length > 0 && (
                    <div className="mb-4" data-testid="loc-trail-map-wrap">
                      <PingTrailMap points={[...trail].reverse()} height="320px" />
                    </div>
                  )}

                  {/* Trail */}
                  <h3 className="text-sm font-semibold text-slate-700 mb-2">Recent trail (last {trail.length})</h3>
                  {trailLoading ? (
                    <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-cyan-500" /></div>
                  ) : trail.length === 0 ? (
                    <p className="text-sm text-slate-400 py-3">No trail entries.</p>
                  ) : (
                    <ol className="relative border-l border-slate-200 ml-2 space-y-3" data-testid="loc-trail-list">
                      {trail.map((p) => (
                        <li key={`${p.user_id}-${p.timestamp}`} className="ml-4">
                          <span className="absolute -left-[5px] mt-1.5 w-2.5 h-2.5 rounded-full bg-cyan-500 border-2 border-white" aria-hidden="true" />
                          <p className="text-sm text-slate-800">
                            {p.latitude?.toFixed(5)}, {p.longitude?.toFixed(5)}
                            {p.accuracy ? <span className="text-slate-500"> (±{Math.round(p.accuracy)}m)</span> : null}
                          </p>
                          <p className="text-xs text-slate-500">{new Date(p.timestamp).toLocaleString()} · {relativeTime(p.timestamp)}</p>
                        </li>
                      ))}
                    </ol>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
